import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  DashboardOutlined,
  ControlOutlined,
  AppstoreOutlined,
  PieChartOutlined,
  SafetyCertificateOutlined,
  RiseOutlined,
  BarChartOutlined,
  AimOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import {
  CREATING,
  HISTORICAL_SCENARIO,
  RISK_WEIGHTED_APPROACH,
  WEALTH_MANAGEMENT,
  Session,
} from 'state';

export type GroupKey =
  | 'overview'
  | 'general'
  | 'instruments'
  | 'allocations'
  | 'limits'
  | 'leverage'
  | 'metrics'
  | 'benchmark'
  | 'sector';

export interface GroupDef {
  key: GroupKey;
  label: string;
  /** short title shown in the workspace header */
  title: string;
  /** one-line workspace description */
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  available: (session: Session) => boolean;
}

/** Ordered group navigation. Mirrors the grouping in the settings map-out. */
export const GROUPS: GroupDef[] = [
  {
    key: 'overview',
    label: 'Overview',
    title: 'Configuration Overview',
    desc: 'A snapshot of every setting group — click a tile to jump in.',
    icon: DashboardOutlined,
    available: () => true,
  },
  {
    key: 'general',
    label: 'General',
    title: 'General Setup',
    desc: 'Session pacing and player-facing behaviour.',
    icon: ControlOutlined,
    available: () => true,
  },
  {
    key: 'instruments',
    label: 'Instruments',
    title: 'Tradable Universe',
    desc: 'Choose the instruments players can trade this session.',
    icon: AppstoreOutlined,
    available: () => true,
  },
  {
    key: 'allocations',
    label: 'Allocations',
    title: 'Strategic Allocations',
    desc: 'Mandate bands per asset-class group.',
    icon: PieChartOutlined,
    available: () => true,
  },
  {
    key: 'limits',
    label: 'Limits & Risk',
    title: 'Limits & Risk',
    desc: 'Per-asset-class limits, hard/soft enforcement and reserves.',
    icon: SafetyCertificateOutlined,
    available: () => true,
  },
  {
    key: 'leverage',
    label: 'Leverage',
    title: 'Leverage & Margin',
    desc: 'Margin requirements and futures contract settings.',
    icon: RiseOutlined,
    available: () => true,
  },
  {
    key: 'metrics',
    label: 'Metrics',
    title: 'Metrics & Limits',
    desc: 'Per-metric limits, enforcement and player visibility.',
    icon: BarChartOutlined,
    available: () => true,
  },
  {
    key: 'benchmark',
    label: 'Benchmark',
    title: 'Benchmark',
    desc: 'The yardstick players are measured against.',
    icon: AimOutlined,
    available: () => true,
  },
  {
    key: 'sector',
    label: 'Sector Alpha',
    title: 'Sector Alpha',
    desc: 'Per-sector alpha attribution against benchmark ETFs.',
    icon: ApartmentOutlined,
    available: (s: Session) => s.scenario_type === HISTORICAL_SCENARIO,
  },
];

export const INVESTMENT_PROFILE_LABELS: { [k: number]: string } = {
  0: 'Conservative',
  1: 'Moderately Conservative',
  2: 'Balanced',
  3: 'Moderately Aggressive',
  4: 'Aggressive',
};

/** Settings store percentages as 0–1 fractions; format for display. */
export const pctFromFraction = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v)
    ? '—'
    : `${Math.round(v * 100)}%`;

/** Most structural settings lock once the session leaves CREATING. */
export const isSessionLocked = (session: Session): boolean =>
  session.status !== CREATING;

export interface BenchmarkComponentLine {
  label: string;
  pct: string;
}

/** The selected shared benchmark constituents (component + weight%). Empty for
 *  constant / individual / unset benchmarks (those have no component list). */
export const benchmarkComponentLines = (
  session: Session,
): BenchmarkComponentLine[] => {
  if (session.allow_individual_benchmark) return [];
  const comps = session.benchmark_compositions ?? [];
  if (comps.length) {
    return comps.map(c => ({
      label: c.ric || String(c.fund),
      pct: pctFromFraction(c.weight),
    }));
  }
  const portfolio = session.benchmark_portfolio_compositions ?? [];
  if (portfolio.length) {
    return portfolio.map(p => ({
      label: String(p.asset),
      pct: p.weight != null ? pctFromFraction(p.weight) : '—',
    }));
  }
  return [];
};

export interface GroupSummary {
  value: string;
  sub?: string;
  tone?: 'pos' | 'neg' | 'muted' | 'default';
}

/** At-a-glance status for a group, used by both the rail and the overview. */
export const summarizeGroup = (
  key: GroupKey,
  session: Session,
): GroupSummary => {
  switch (key) {
    case 'general':
      return {
        value:
          session.scenario_type === HISTORICAL_SCENARIO
            ? `${session.historical_sleep_time ?? '—'}s / step`
            : 'Behaviour',
        sub: `Sound ${session.allow_sound ? 'on' : 'off'} · Coupons ${
          session.pay_coupons ? 'on' : 'off'
        }`,
      };
    case 'instruments':
      return {
        value: `${session.underlying_assets?.length ?? 0}`,
        sub: 'instruments selected',
      };
    case 'allocations':
      return session.management_type === WEALTH_MANAGEMENT
        ? {
            value:
              INVESTMENT_PROFILE_LABELS[session.investment_profile as number] ??
              'Wealth',
            sub: 'wealth mandate',
          }
        : {
            value: `${session.asset_group_allocations?.length ?? 0}`,
            sub: 'allocation bands',
          };
    case 'limits':
      return {
        value: session.hard_limits ? 'Hard' : 'Soft',
        sub:
          session.risk_approach === RISK_WEIGHTED_APPROACH
            ? 'risk-weighted'
            : 'cash approach',
        tone: session.hard_limits ? 'default' : 'muted',
      };
    case 'leverage':
      return {
        value: `FX ${pctFromFraction(session.fx_leverage)}`,
        sub: `Bonds ${pctFromFraction(
          session.bond_leverage,
        )} · Eq ${pctFromFraction(
          session.equity_leverage,
        )} · ETF Eq ${pctFromFraction(
          session.etf_equities_leverage,
        )} · ETF Cmdty ${pctFromFraction(session.etf_commodities_leverage)}`,
      };
    case 'metrics': {
      const ml = session.metrics_limits ?? [];
      const enforced = ml.filter(m => m.enforced).length;
      const visible = ml.filter(m => m.visible).length;
      return {
        value: `${ml.length}`,
        sub: `${enforced} enforced · ${visible} visible`,
      };
    }
    case 'benchmark': {
      let value = 'Not set';
      if (session.benchmark_constant) {
        value = `Constant ${pctFromFraction(session.benchmark_constant)}`;
      } else if (session.allow_individual_benchmark) {
        value = 'Individual';
      } else if (session.benchmark_compositions?.length) {
        value = `Composition (${session.benchmark_compositions.length})`;
      } else if (session.benchmark_portfolio_compositions?.length) {
        value = `Portfolio (${session.benchmark_portfolio_compositions.length})`;
      }
      return { value, sub: 'yardstick' };
    }
    case 'sector': {
      const settings = session.sector_alpha_settings;
      const sectors = settings?.session_sectors ?? [];
      const set = sectors.filter(s => s.benchmark_asset_id != null).length;
      return {
        value: session.per_sector_alpha ? 'On' : 'Off',
        sub: session.per_sector_alpha
          ? `${set}/${sectors.length} sectors set`
          : 'monitoring off',
        tone: session.per_sector_alpha ? 'pos' : 'muted',
      };
    }
    default:
      return { value: '' };
  }
};

/** Measure distance from viewport top and fill the rest so the page frame
 *  never scrolls regardless of header/banner height. (Same technique as the
 *  Analysis Studio shell.) */
export const useFillHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(440, window.innerHeight - top - 10));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return { ref, height };
};
